/**
 * Global IST patch for all date display across the AxialUp app.
 *
 * Two fixes:
 *   1. ISO strings without a timezone marker (e.g. "2026-06-07T05:52:00")
 *      are intended by the backend to be UTC, but ECMAScript treats them
 *      as LOCAL time. This caused production timestamps to be off by the
 *      browser's offset from UTC (5:30 hrs in IST → showed 5:52 AM when
 *      it should have been 11:22 AM IST).
 *
 *      Solution: monkey-patch the Date constructor so that any naive
 *      ISO 8601 string is parsed as UTC by appending 'Z'.
 *
 *   2. Date.prototype.toLocaleString / toLocaleDateString / toLocaleTimeString
 *      use the browser's tz by default. If a remote user (e.g. CA, vendor)
 *      opens the app from outside India, all timestamps would shift to
 *      their local zone — confusing.
 *
 *      Solution: monkey-patch these formatters to default to
 *      timeZone: 'Asia/Kolkata' unless the caller explicitly overrides it.
 *
 * Import this file ONCE at the top of src/index.js so it runs before any
 * application code touches Date.
 */

const OriginalDate = Date;
// Match naive ISO 8601: "YYYY-MM-DDTHH:MM:SS" with optional fractional sec,
// but NO timezone offset (Z, +/-HH:MM).
const NAIVE_ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function normalizeNaiveIso(args) {
  if (args.length === 1 && typeof args[0] === 'string' && NAIVE_ISO.test(args[0])) {
    args = [args[0] + 'Z'];
  }
  return args;
}

const PatchedDate = function PatchedDate(...args) {
  args = normalizeNaiveIso(args);
  if (this instanceof PatchedDate) {
    // Called with `new` — construct a real Date subclass-style.
    return new OriginalDate(...args);
  }
  // Called without `new` (rare, returns string per spec).
  return OriginalDate(...args);
};

// Preserve prototype chain so `instanceof Date` and method calls still work.
PatchedDate.prototype = OriginalDate.prototype;
Object.setPrototypeOf(PatchedDate, OriginalDate);

// Mirror static methods.
PatchedDate.now = OriginalDate.now.bind(OriginalDate);
PatchedDate.parse = (s) => {
  if (typeof s === 'string' && NAIVE_ISO.test(s)) s = s + 'Z';
  return OriginalDate.parse(s);
};
PatchedDate.UTC = OriginalDate.UTC.bind(OriginalDate);

// eslint-disable-next-line no-global-assign
Date = PatchedDate;
if (typeof window !== 'undefined') {
  window.Date = PatchedDate;
}

// ── Default formatter timezone to Asia/Kolkata (IST) ────────────────────
const proto = OriginalDate.prototype;
const origLocaleString = proto.toLocaleString;
const origLocaleDateString = proto.toLocaleDateString;
const origLocaleTimeString = proto.toLocaleTimeString;

function injectIst(opts) {
  // Caller-provided timeZone wins. Only inject when missing.
  if (opts && opts.timeZone) return opts;
  return { timeZone: 'Asia/Kolkata', ...(opts || {}) };
}

// eslint-disable-next-line no-extend-native
proto.toLocaleString = function (locale, opts) {
  return origLocaleString.call(this, locale || 'en-IN', injectIst(opts));
};
// eslint-disable-next-line no-extend-native
proto.toLocaleDateString = function (locale, opts) {
  return origLocaleDateString.call(this, locale || 'en-IN', injectIst(opts));
};
// eslint-disable-next-line no-extend-native
proto.toLocaleTimeString = function (locale, opts) {
  return origLocaleTimeString.call(this, locale || 'en-IN', injectIst(opts));
};

export {};
